<!-- EditSubscriptionForm.vue — exibe TODOS os campos do SubscriptionReadDto -->
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { usePlanStore } from "@/stores/usePlanStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { useDraggableModal } from "@/composables/useDraggableModal";

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// ====================================================================
// PROPS
// ====================================================================
const props = defineProps({
  subscription: {
    type: Object,
    required: true,
  },
});

const subscriptionStore = useSubscriptionStore();

// ── PATCH: store de planos usada apenas para popular o select de Plano ──
const planStore = usePlanStore();

const emit = defineEmits(["close", "refresh"]);

// ====================================================================
// ESTADO — apenas os 5 campos editáveis pelo backend
// Todos os demais campos do DTO são exibidos como readonly
// ====================================================================
const form = ref({
  subscriptionId: null,
  planId: "", // ✏️ editável
  status: "PendingPayment", // ✏️ editável
  paymentStatus: "None", // ✏️ editável
  preapprovalStatus: "None", // ✏️ PATCH: editável
  statusDetail: "", // ✏️ PATCH: editável
  paymentMethod: "", // ✏️ editável
  mpPaymentId: "", // ✏️ PATCH: editável
  mpSubscriptionId: "", // ✏️ PATCH: editável
  startDate: "", // ✏️ PATCH: editável
  endDate: "", // ✏️ editável
});

const errors = ref({});
const isSubmitting = ref(false);
const isLoading = ref(true);

// ── PATCH: controla apenas o carregamento do select de planos ──
const isLoadingPlans = ref(false);

// ====================================================================
// HELPERS
// ====================================================================
// ── PATCH: conversão manual UTC <-> UTC-3 para StartDate/EndDate ──
// Banco/API trabalham em UTC.
// Front exibe UTC-3.
// Ex.: banco 2026-05-12T03:00:00 => front 2026-05-12T00:00.
const UTC_MINUS_3_OFFSET_MINUTES = -180;

function pad2(value) {
  return String(value).padStart(2, "0");
}

function parseBackendUtcDate(value) {
  if (!value) return null;

  const raw = String(value).trim();

  // Aceita:
  // "2026-05-12T03:40:19"
  // "2026-05-12T03:40:19Z"
  // "2026-05-12 03:40:19"
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (match) {
    const [, year, month, day, hour, minute, second = "0"] = match;

    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      )
    );
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDatetimeLocalUtcMinus3(value) {
  const utcDate = parseBackendUtcDate(value);
  if (!utcDate) return "";

  const shifted = new Date(
    utcDate.getTime() + UTC_MINUS_3_OFFSET_MINUTES * 60 * 1000
  );

  return [
    shifted.getUTCFullYear(),
    "-",
    pad2(shifted.getUTCMonth() + 1),
    "-",
    pad2(shifted.getUTCDate()),
    "T",
    pad2(shifted.getUTCHours()),
    ":",
    pad2(shifted.getUTCMinutes()),
  ].join("");
}

function fromDatetimeLocalUtcMinus3ToUtc(value) {
  if (!value) return null;

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
  );

  if (!match) return null;

  const [, year, month, day, hour, minute] = match;

  // O usuário visualiza/digita em UTC-3.
  // Para gravar em UTC, soma 3 horas.
  const utcMs =
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      0
    ) -
    UTC_MINUS_3_OFFSET_MINUTES * 60 * 1000;

  return new Date(utcMs).toISOString();
}

// Exibe "—" para null/undefined/"" — usado nos campos readonly
function display(v) {
  return v != null && v !== "" ? v : "—";
}

// ====================================================================
// PATCH: PLANOS — opções do select
// Usa a lista já carregada pelo usePlanStore.fetchPlans().
// Se por algum motivo o plano atual não vier na lista, ele é mantido
// como opção para evitar select vazio.
// ====================================================================
const planOptions = computed(() => {
  const plans = Array.isArray(planStore.plans) ? [...planStore.plans] : [];

  const currentPlanId = Number(props.subscription?.planId);

  if (
    currentPlanId > 0 &&
    !plans.some((plan) => Number(plan.planId) === currentPlanId)
  ) {
    plans.unshift({
      planId: currentPlanId,
      name: props.subscription?.planName || `Plano #${currentPlanId}`,
      price: null,
      isActive: true,
    });
  }

  return plans;
});

function formatBillingCycle(billingCycle) {
  switch (String(billingCycle || "").toLowerCase()) {
    case "monthly":
      return "Recorrente mensal";
    case "yearly":
      return "Recorrente anual";
    case "onetime":
      return "Pagamento único";
    default:
      return "Tipo não informado";
  }
}

function formatPlanOption(plan) {
  const id = plan?.planId ?? "";
  const name = plan?.name || plan?.planName || `Plano #${id}`;

  const price =
    plan?.price !== null &&
    plan?.price !== undefined &&
    !Number.isNaN(Number(plan.price))
      ? ` — R$ ${Number(plan.price).toFixed(2).replace(".", ",")}`
      : "";

  const billing = ` — ${formatBillingCycle(plan?.billingCycle)}`;

  const inactive = plan?.isActive === false ? " — inativo" : "";

  return `#${id} — ${name}${price}${billing}${inactive}`;
}

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 3000);
}
function showErrorToast(msg) {
  toastType.value = "error";
  toastMessage.value = msg;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(msg) {
  toastType.value = "success";
  toastMessage.value = msg;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// LIFECYCLE
// ====================================================================
onMounted(async () => {
  document.addEventListener("keydown", handleEscKey);

  try {
    const s = props.subscription;

    // ── PATCH: carrega os planos antes de liberar o formulário ──
    isLoadingPlans.value = true;
    await planStore.fetchPlans();

    if (planStore.error) {
      throw new Error(
        planStore.error?.message ||
          planStore.error ||
          "Não foi possível carregar a lista de planos."
      );
    }

    form.value = {
      subscriptionId: s.subscriptionId ?? null,
      planId: String(s.planId ?? ""),
      status: normalizeStatus(s.status),
      paymentStatus: normalizePaymentStatus(s.paymentStatus),

      preapprovalStatus: normalizePreapprovalStatus(s.preapprovalStatus),
      statusDetail: s.statusDetail ?? "",
      paymentMethod: s.paymentMethod ?? "",
      mpPaymentId: s.mpPaymentId ?? "",
      mpSubscriptionId: s.mpSubscriptionId ?? "",

      // ── PATCH: exibe datas UTC do banco como UTC-3 no frontend ──
      startDate: toDatetimeLocalUtcMinus3(s.startDate),
      endDate: toDatetimeLocalUtcMinus3(s.endDate),
    };
  } catch (err) {
    console.error("[EditSubscriptionForm] erro ao popular formulário:", err);
    showErrorToast(
      err?.message || "Não foi possível carregar os dados da assinatura."
    );
  } finally {
    isLoadingPlans.value = false;
    isLoading.value = false;
    await nextTick();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

function handleEscKey(e) {
  if (e.key === "Escape") closeModal();
}

// ====================================================================
// NORMALIZAÇÃO — converte valores da API para os <option value="...">
// ====================================================================
const STATUS_MAP = {
  active: "Active",
  cancelled: "Cancelled",
  expired: "Expired",
  upgraded: "Upgraded",
  pendingpayment: "PendingPayment",
  pending_payment: "PendingPayment",
  0: "Active",
  1: "Cancelled",
  2: "Expired",
  3: "Upgraded",
  4: "PendingPayment",
};
function normalizeStatus(val) {
  if (val == null) return "PendingPayment";
  const key = String(val).trim().toLowerCase().replace(/[_\s]/g, "");
  return STATUS_MAP[key] ?? STATUS_MAP[Number(val)] ?? "PendingPayment";
}

const PAYMENT_STATUS_MAP = {
  none: "None",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  refunded: "Refunded",
  refundfailed: "RefundFailed",
  0: "None",
  1: "Pending",
  2: "Approved",
  3: "Rejected",
  4: "Cancelled",
  5: "Refunded",
  6: "RefundFailed",
};
function normalizePaymentStatus(val) {
  if (val == null) return "None";
  const key = String(val).trim().toLowerCase();
  return PAYMENT_STATUS_MAP[key] ?? PAYMENT_STATUS_MAP[Number(val)] ?? "None";
}

function normalizePreapprovalStatus(val) {
  if (val == null) return "None";

  const map = {
    0: "None",
    none: "None",

    1: "Pending",
    pending: "Pending",

    2: "Authorized",
    authorized: "Authorized",

    3: "Cancelled",
    cancelled: "Cancelled",

    // ✅ NOVO
    4: "Paused",
    paused: "Paused",
  };

  const key = String(val).trim().toLowerCase();
  const n = Number(val);

  return !isNaN(n) ? map[n] ?? "None" : map[key] ?? "None";
}

// ====================================================================
// VALIDAÇÃO
// ====================================================================
function validateForm() {
  errors.value = {};

  const planId = parseInt(form.value.planId);

  // ── PATCH: validação agora trata Plano como seleção, não input livre ──
  if (!form.value.planId || isNaN(planId) || planId <= 0)
    errors.value.planId = "Selecione um plano válido.";

  if (!form.value.startDate)
    errors.value.startDate = "Informe a data de início da assinatura.";

  return Object.keys(errors.value).length === 0;
}

// ====================================================================
// SUBMIT — payload idêntico ao SubscriptionUpdateDto do backend
// ====================================================================
async function handleSubmit() {
  if (isSubmitting.value || isLoading.value) return;

  if (!validateForm()) {
    showErrorToast("Verifique os campos obrigatórios antes de salvar.");
    return;
  }

  // ── PATCH: converte datas exibidas em UTC-3 para UTC antes de enviar ──
  const startDateUtc = fromDatetimeLocalUtcMinus3ToUtc(form.value.startDate);
  const endDateUtc = form.value.endDate
    ? fromDatetimeLocalUtcMinus3ToUtc(form.value.endDate)
    : null;

  if (!startDateUtc) {
    errors.value.startDate = "Informe uma data de início válida.";
    showErrorToast("Informe uma data de início válida.");
    return;
  }

  if (form.value.endDate && !endDateUtc) {
    showErrorToast("Informe uma data de término válida ou deixe o campo vazio.");
    return;
  }

  isSubmitting.value = true;

  try {
    await subscriptionStore.updateSubscription({
      subscriptionId: form.value.subscriptionId,
      planId: parseInt(form.value.planId),

      // ── PATCH: envia datas em UTC para o backend/base ───────────
      startDate: startDateUtc,
      endDate: endDateUtc,

      status: form.value.status,
      paymentStatus: form.value.paymentStatus,

      preapprovalStatus: form.value.preapprovalStatus,
      statusDetail: form.value.statusDetail?.trim()
        ? form.value.statusDetail
        : null,

      paymentMethod: form.value.paymentMethod || null,

      mpPaymentId: form.value.mpPaymentId?.trim()
        ? form.value.mpPaymentId.trim()
        : null,

      mpSubscriptionId: form.value.mpSubscriptionId?.trim()
        ? form.value.mpSubscriptionId.trim()
        : null,
    });

    showSuccessToast("Assinatura atualizada com sucesso.");

    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400);
  } catch (error) {
    const data = error?.response?.data;
    let msg = "Ocorreu um erro ao atualizar a assinatura.";

    if (data?.errors) {
      const msgs = Object.values(data.errors).flat().filter(Boolean);
      if (msgs.length) msg = msgs.join("\n");
    } else if (data?.message) {
      msg = data.message;
    } else if (error?.message) {
      msg = error.message;
    }

    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// FECHAR
// ====================================================================
function closeModal() {
  if (!isSubmitting.value) emit("close");
}

// ====================================================================
// OPTIONS DOS SELECTS
// ====================================================================
const statusOptions = [
  { value: "Active", label: "Ativo" },
  { value: "PendingPayment", label: "Aguardando Pag." },
  { value: "Cancelled", label: "Cancelado" },
  { value: "Expired", label: "Expirado" },
  { value: "Upgraded", label: "Atualizado" },
];

const paymentStatusOptions = [
  { value: "None", label: "Nenhum" },
  { value: "Pending", label: "Pendente" },
  { value: "Approved", label: "Aprovado" },
  { value: "Rejected", label: "Rejeitado" },
  { value: "Cancelled", label: "Cancelado" },
  { value: "Refunded", label: "Reembolsado" },
  { value: "RefundFailed", label: "Estorno não confirmado" },
];

// ── PATCH: options do PreapprovalStatus ─────────────────────
const preapprovalStatusOptions = [
  { value: "None", label: "Nenhum" },
  { value: "Pending", label: "Pendente" },
  { value: "Authorized", label: "Autorizado" },
  { value: "Cancelled", label: "Cancelado" },
  { value: "Paused", label: "Pausado" }, // ✅ NOVO
];
</script>

<template>
  <div class="modal-backdrop" @click.self="closeModal">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- ── HEADER ─────────────────────────────────────────── -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">
          Editar Assinatura
          <span v-if="form.subscriptionId" class="sub-id"
            >#{{ form.subscriptionId }}</span
          >
        </h2>
        <button
          @click="closeModal"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0
                 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293
                 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <!-- Skeleton -->
      <div v-if="isLoading" class="loading-state">
        <svg class="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
        <span>Carregando dados da assinatura...</span>
      </div>

      <form v-else @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">
          <!-- ══ 1. IDENTIFICAÇÃO ═══════════════════════════════
     SubscriptionId · UserId/UserName
     Plano editável · IsRecurring
══════════════════════════════════════════════════════ -->
          <p class="section-label">Identificação</p>
          <div class="form-grid-2">
            <!-- ID Assinatura — readonly -->
            <div class="form-group">
              <label class="form-label">ID Assinatura</label>
              <div class="readonly-field">
                <span class="readonly-value">{{
                  display(props.subscription.subscriptionId)
                }}</span>
              </div>
            </div>

            <!-- Usuário — readonly (nome + id) -->
            <div class="form-group">
              <label class="form-label">Usuário</label>
              <div class="readonly-field">
                <span class="readonly-value">
                  {{ props.subscription.userName || props.subscription.userId }}
                </span>
                <span v-if="props.subscription.userName" class="readonly-id">
                  #{{ props.subscription.userId }}
                </span>
              </div>
            </div>
          </div>

          <!-- ── PATCH: Plano fica apenas como select editável ── -->
          <div class="form-grid-plan-row" style="margin-top: 0.75rem">
            <!-- ✏️ PATCH: Plano editável via select -->
            <div class="form-group plan-select-group">
              <label class="form-label">
                Plano <span class="required-star">*</span>
                <span class="edit-badge">editável</span>
              </label>

              <select
                v-model="form.planId"
                class="input-base select-base"
                :class="{ 'is-invalid': errors.planId }"
                :disabled="
                  isLoadingPlans || isSubmitting || planOptions.length === 0
                "
              >
                <option value="" disabled>
                  {{
                    isLoadingPlans
                      ? "Carregando planos..."
                      : "Selecione um plano"
                  }}
                </option>

                <option
                  v-for="plan in planOptions"
                  :key="plan.planId"
                  :value="String(plan.planId)"
                >
                  {{ formatPlanOption(plan) }}
                </option>
              </select>

              <span v-if="errors.planId" class="error-msg">
                {{ errors.planId }}
              </span>

              <span
                v-else-if="planOptions.length === 0 && !isLoadingPlans"
                class="error-msg"
              >
                Nenhum plano disponível para seleção.
              </span>
            </div>
          </div>

          <!-- ══ 2. STATUS ══════════════════════════════════════
               Status (✏️) · PaymentStatus (✏️) · PreapprovalStatus (readonly)
               DowngradedToFreeOnThisRequest (readonly)
          ══════════════════════════════════════════════════════ -->
          <p class="section-label" style="margin-top: 1.25rem">Status</p>
          <div class="form-grid-3">
            <!-- ✏️ Status -->
            <div class="form-group">
              <label class="form-label">
                Status <span class="required-star">*</span>
                <span class="edit-badge">editável</span>
              </label>
              <select v-model="form.status" class="input-base select-base">
                <option
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <!-- ✏️ Status de Pagamento -->
            <div class="form-group">
              <label class="form-label">
                Pgto. <span class="edit-badge">editável</span>
              </label>
              <select
                v-model="form.paymentStatus"
                class="input-base select-base"
              >
                <option
                  v-for="opt in paymentStatusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <!-- ✏️ PATCH: PreapprovalStatus editável -->
            <div class="form-group">
              <label class="form-label">
                Preapproval
                <span class="edit-badge">editável</span>
              </label>
              <select
                v-model="form.preapprovalStatus"
                class="input-base select-base"
              >
                <option
                  v-for="opt in preapprovalStatusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- DowngradedToFreeOnThisRequest -->
          <div class="form-grid-1" style="margin-top: 0.75rem">
            <div class="form-group">
              <label class="form-label"
                >Downgrade para Free nesta requisição?</label
              >
              <div class="readonly-field">
                <span class="readonly-value">
                  {{
                    props.subscription.downgradedToFreeOnThisRequest
                      ? "Sim"
                      : "Não"
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- ══ 3. DETALHE DE STATUS ═══════════════════════════
               StatusDetail · SystemMessage
          ══════════════════════════════════════════════════════ -->
          <p class="section-label" style="margin-top: 1.25rem">
            Detalhe de Status
          </p>
          <div class="form-grid-1">
            <!-- ✏️ PATCH: Status Detail editável -->
            <div class="form-group">
              <label class="form-label">
                Status Detail
                <span class="optional-tag">opcional</span>
                <span class="edit-badge">editável</span>
              </label>
              <textarea
                v-model="form.statusDetail"
                class="input-base textarea-base"
                rows="4"
                placeholder="Detalhe administrativo do status da assinatura..."
              ></textarea>
            </div>

            <div class="form-group">
              <label class="form-label">System Message</label>
              <div class="readonly-field readonly-field--multiline">
                <span class="readonly-value">{{
                  display(props.subscription.systemMessage)
                }}</span>
              </div>
            </div>
          </div>

          <!-- ══ 4. PAGAMENTO ═══════════════════════════════════
               PaymentMethod (✏️) · MpPaymentId · MpSubscriptionId
               InitPoint · SandboxInitPoint
          ══════════════════════════════════════════════════════ -->
          <p class="section-label" style="margin-top: 1.25rem">Pagamento</p>
          <div class="form-grid-2">
            <!-- ✏️ Método de Pagamento -->
            <div class="form-group">
              <label class="form-label">
                Método <span class="optional-tag">opcional</span>
                <span class="edit-badge">editável</span>
              </label>
              <input
                type="text"
                v-model="form.paymentMethod"
                class="input-base"
                placeholder="Ex.: credit_card, pix, free…"
                maxlength="50"
              />
            </div>

            <!-- MP Payment ID — readonly -->
            <!-- ✏️ PATCH: MP Payment ID editável -->
            <div class="form-group">
              <label class="form-label">
                MP Payment ID
                <span class="optional-tag">opcional</span>
                <span class="edit-badge">editável</span>
              </label>
              <input
                type="text"
                v-model="form.mpPaymentId"
                class="input-base mono-input"
                placeholder="Ex.: 157000549947"
              />
            </div>

            <!-- ✏️ PATCH: MP Subscription ID editável -->
            <div class="form-group col-span-2">
              <label class="form-label">
                MP Subscription ID
                <span class="optional-tag">opcional</span>
                <span class="edit-badge">editável</span>
              </label>
              <input
                type="text"
                v-model="form.mpSubscriptionId"
                class="input-base mono-input"
                placeholder="Ex.: preapproval id do Mercado Pago"
              />
            </div>

            <!-- Init Point — readonly, pode ser longo -->
            <div class="form-group col-span-2">
              <label class="form-label">Init Point</label>
              <div class="readonly-field readonly-field--wrap">
                <span class="readonly-value mono">{{
                  display(props.subscription.initPoint)
                }}</span>
              </div>
            </div>

            <!-- Sandbox Init Point — readonly -->
            <div class="form-group col-span-2">
              <label class="form-label">Sandbox Init Point</label>
              <div class="readonly-field readonly-field--wrap">
                <span class="readonly-value mono">{{
                  display(props.subscription.sandboxInitPoint)
                }}</span>
              </div>
            </div>
          </div>

          <!-- ══ 5. VIGÊNCIA ════════════════════════════════════
     StartDate (✏️) · EndDate (✏️)
══════════════════════════════════════════════════════ -->
          <p class="section-label" style="margin-top: 1.25rem">Vigência</p>
          <div class="form-grid-2">
            <!-- ✏️ PATCH: Data de Início editável -->
            <div class="form-group">
              <label class="form-label">
                Data de Início
                <span class="required-star">*</span>
                <span class="edit-badge">editável</span>
              </label>
              <input
                type="datetime-local"
                v-model="form.startDate"
                class="input-base"
                :class="{ 'is-invalid': errors.startDate }"
              />
              <span v-if="errors.startDate" class="error-msg">{{
                errors.startDate
              }}</span>
            </div>

            <!-- ✏️ Data de Término -->
            <div class="form-group">
              <label class="form-label">
                Data de Término
                <span class="optional-tag">opcional</span>
                <span class="edit-badge">editável</span>
              </label>
              <input
                type="datetime-local"
                v-model="form.endDate"
                class="input-base"
              />
              <span class="hint-msg"
                >Deixe em branco para assinaturas sem prazo.</span
              >
            </div>
          </div>

          <!-- ══ 6. AUDITORIA ═══════════════════════════════════
               CreatedAt · UpdatedAt
          ══════════════════════════════════════════════════════ -->
          <p class="section-label" style="margin-top: 1.25rem">Auditoria</p>
          <div class="form-grid-2">
            <div class="form-group">
              <label class="form-label">Criado em</label>
              <div class="readonly-field">
                <span class="readonly-value">
                  {{ formatDateTimeForDisplay(props.subscription.createdAt) }}
                </span>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Atualizado em</label>
              <div class="readonly-field">
                <span class="readonly-value">
                  {{ formatDateTimeForDisplay(props.subscription.updatedAt) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Aviso final -->
          <div class="info-banner" style="margin-top: 1.25rem">
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z"
              />
            </svg>
            <span>
              Campos marcados com
              <span class="edit-badge" style="margin: 0 2px">editável</span>
              serão atualizados ao salvar. Os demais são somente leitura.
            </span>
          </div>
        </div>

        <!-- ── FOOTER ──────────────────────────────────────── -->
        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              />
            </svg>
            <span>{{
              isSubmitting ? "Salvando..." : "Salvar Alterações"
            }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button type="button" class="toast-close" @click="showToast = false">
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 46rem;
  max-height: calc(100vh - 40px);
  margin-bottom: 40px;
}
.modal-panel > form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.modal-header {
  flex-shrink: 0;
}

.sub-id {
  font-size: 0.82rem;
  font-weight: 400;
  color: var(--text-subtle);
}

.loading-state {
  flex: 1 1 auto;
}

.modal-footer {
  flex-shrink: 0;
}

/* ── SECTION LABEL ─────────────────────────────────────────── */
.section-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-subtle);
  margin: 0 0 0.6rem 0;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid var(--border-color);
}

/* ── GRIDS ─────────────────────────────────────────────────── */
.form-grid-1 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}
.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}
.form-grid-3 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

@media (min-width: 480px) {
  .form-grid-2 {
    grid-template-columns: 1fr 1fr;
  }
  .form-grid-3 {
    grid-template-columns: 1fr 1fr;
  }
}
@media (min-width: 640px) {
  .form-grid-3 {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.col-span-2 {
  grid-column: 1 / -1;
}

.form-grid-plan-row {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.75fr);
  gap: 0.9rem;
  align-items: start;
}

.plan-select-group {
  min-width: 0;
}
.required-star {
  color: var(--danger);
  font-weight: 700;
}
.optional-tag {
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--text-muted);
  background-color: var(--surface-3);
  border: 1px solid var(--neutral-200);
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
}

/* Badge "editável" — destaca visualmente os campos que serão salvos */
.edit-badge {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #6d28d9;
  background-color: var(--brand-soft);
  border: 1px solid #c4b5fd;
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
}

/* ── CAMPO READONLY ────────────────────────────────────────── */
.readonly-field {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  background-color: var(--surface-2);
  min-height: 2.25rem;
}
/* URLs e IDs longos — quebra linha */
.readonly-field--wrap {
  align-items: flex-start;
  word-break: break-all;
  white-space: normal;
}
/* Campos de texto multilinha (statusDetail, systemMessage) */
.readonly-field--multiline {
  align-items: flex-start;
  min-height: 3rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.readonly-value {
  font-size: 0.875rem;
  color: var(--text-dark);
}
.readonly-value.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  color: var(--text-light);
}
.readonly-id {
  font-size: 0.73rem;
  color: var(--text-subtle);
}

/* ── PATCH: textarea e inputs técnicos editáveis ───────────── */
.textarea-base {
  min-height: 5.5rem;
  resize: vertical;
  white-space: pre-wrap;
}

.mono-input {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

/* SELECT */
.select-base {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  background-size: 1.1rem;
  padding-right: 2rem;
  height: 2.25rem;
}

/* ── MENSAGENS ─────────────────────────────────────────────── */
.error-msg {
  color: var(--danger);
  font-size: 0.78rem;
}
.hint-msg {
  color: var(--text-subtle);
  font-size: 0.74rem;
  margin-top: 0.1rem;
}

/* ── INFO BANNER ───────────────────────────────────────────── */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background-color: var(--info-bg);
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 0.8rem;
  color: var(--link-hover);
}

</style>